import { BaseFeature } from '../../core/base-feature.js';

/**
 * CameraMonitorFeature - Frontend feature for UniFi Protect camera monitoring
 * Handles: connection, camera listing, snapshots, livestreaming, configuration
 */
export class CameraMonitorFeature extends BaseFeature {
    constructor(coreAPI) {
        super(coreAPI);

        // State
        this.connected = false;
        this.cameras = [];
        this.snapshots = new Map(); // cameraId → base64
        this.classifications = []; // recent classifications

        // Event callbacks for React components
        this.updateCallbacks = new Set();
        this.snapshotCallbacks = new Set();
        this.classificationCallbacks = new Set();
        this.livestreamCallbacks = new Map(); // cameraId → Set<callback>

        // Pending promises for request/response
        this._pendingRequests = new Map();
    }

    static getMetadata() {
        return {
            id: 'camera-monitor',
            name: 'Camera Monitor',
            version: 'v1.0',
            capabilities: [
                'connect',
                'disconnect',
                'get_cameras',
                'get_snapshot',
                'start_livestream',
                'stop_livestream',
                'configure',
                'get_config',
                'get_status'
            ],
            dependencies: []
        };
    }

    async initialize() {
        console.log('[CameraMonitor] Initializing...');
        console.log('[CameraMonitor] Initialized');
    }

    async handleMessage(message) {
        const { action, cmd, payload, messageId, fromId } = message;
        const command = cmd || action;

        switch (command) {
            // Responses
            case 'connect_response':
                return this._handleConnectResponse(payload);
            case 'disconnect_response':
                return this.handleResponse('disconnect', payload);
            case 'get_cameras_response':
                return this._handleGetCamerasResponse(payload);
            case 'get_snapshot_response':
                return this._handleSnapshotResponse(payload);
            case 'start_livestream_response':
                return this.handleResponse('start_livestream', payload);
            case 'stop_livestream_response':
                return this.handleResponse('stop_livestream', payload);
            case 'configure_response':
                return this.handleResponse('configure', payload);
            case 'get_config_response':
                return this.handleResponse('get_config', payload);
            case 'get_status_response':
                return this.handleResponse('get_status', payload);

            // Real-time updates (from Sacred Timeline events pushed via DataChannel)
            case 'snapshot_update':
                return this._handleSnapshotUpdate(payload);
            case 'classification_update':
                return this._handleClassificationUpdate(payload);
            case 'livestream_segment':
                return this._handleLivestreamSegment(payload);
            case 'initial_state':
                return this._handleInitialState(payload);

            default:
                console.warn(`[CameraMonitor] Unknown command: ${command}`);
        }
    }

    // =====================================
    // Response Handlers
    // =====================================

    _handleConnectResponse(payload) {
        if (payload?.success) {
            this.connected = true;
            this.cameras = payload.cameras || [];
            this.notifyUpdateListeners();
        }
        this.handleResponse('connect', payload);
    }

    _handleGetCamerasResponse(payload) {
        if (payload?.success) {
            this.cameras = payload.cameras || [];
            this.notifyUpdateListeners();
        }
        this.handleResponse('get_cameras', payload);
    }

    _handleSnapshotResponse(payload) {
        if (payload?.success && payload.cameraId) {
            this.snapshots.set(payload.cameraId, payload.imageBase64);
            this._notifySnapshotListeners(payload.cameraId, payload.imageBase64);
        }
        this.handleResponse('get_snapshot', payload);
    }

    _handleSnapshotUpdate(payload) {
        if (payload?.cameraId && payload?.imageBase64) {
            this.snapshots.set(payload.cameraId, payload.imageBase64);
            this._notifySnapshotListeners(payload.cameraId, payload.imageBase64);
        }
    }

    _handleClassificationUpdate(payload) {
        this.classifications.unshift(payload);
        if (this.classifications.length > 100) {
            this.classifications.pop();
        }
        this._notifyClassificationListeners(payload);
    }

    _handleLivestreamSegment(payload) {
        const { cameraId, data, isInit } = payload;
        const callbacks = this.livestreamCallbacks.get(cameraId);
        if (callbacks) {
            for (const cb of callbacks) {
                try {
                    cb({ data, isInit });
                } catch (err) {
                    console.error('[CameraMonitor] Livestream callback error:', err);
                }
            }
        }
    }

    _handleInitialState(payload) {
        if (payload.connected !== undefined) {
            this.connected = payload.connected;
        }
        if (payload.cameras) {
            this.cameras = payload.cameras;
        }
        this.notifyUpdateListeners();
    }

    handleResponse(requestType, payload) {
        const resolve = this._pendingRequests.get(requestType);
        if (resolve) {
            this._pendingRequests.delete(requestType);
            resolve(payload);
        }
    }

    // =====================================
    // API Methods
    // =====================================

    async connect(address, username, password) {
        return new Promise((resolve) => {
            this.sendMessage('connect', { address, username, password });
            this._pendingRequests.set('connect', resolve);
        });
    }

    async disconnect() {
        return new Promise((resolve) => {
            this.sendMessage('disconnect', {});
            this._pendingRequests.set('disconnect', resolve);
        });
    }

    async getCameras() {
        return new Promise((resolve) => {
            this.sendMessage('get_cameras', {});
            this._pendingRequests.set('get_cameras', resolve);
        });
    }

    async getSnapshot(cameraId) {
        return new Promise((resolve) => {
            this.sendMessage('get_snapshot', { cameraId });
            this._pendingRequests.set('get_snapshot', resolve);
        });
    }

    async startLivestream(cameraId, channel = 0) {
        return new Promise((resolve) => {
            this.sendMessage('start_livestream', { cameraId, channel });
            this._pendingRequests.set('start_livestream', resolve);
        });
    }

    async stopLivestream(cameraId) {
        return new Promise((resolve) => {
            this.sendMessage('stop_livestream', { cameraId });
            this._pendingRequests.set('stop_livestream', resolve);
        });
    }

    async configure(config) {
        return new Promise((resolve) => {
            this.sendMessage('configure', config);
            this._pendingRequests.set('configure', resolve);
        });
    }

    async getConfig() {
        return new Promise((resolve) => {
            this.sendMessage('get_config', {});
            this._pendingRequests.set('get_config', resolve);
        });
    }

    async getStatus() {
        return new Promise((resolve) => {
            this.sendMessage('get_status', {});
            this._pendingRequests.set('get_status', resolve);
        });
    }

    // =====================================
    // Event Subscriptions (for React)
    // =====================================

    onUpdate(callback) {
        this.updateCallbacks.add(callback);
        return () => this.updateCallbacks.delete(callback);
    }

    onSnapshot(callback) {
        this.snapshotCallbacks.add(callback);
        return () => this.snapshotCallbacks.delete(callback);
    }

    onClassification(callback) {
        this.classificationCallbacks.add(callback);
        return () => this.classificationCallbacks.delete(callback);
    }

    onLivestreamSegment(cameraId, callback) {
        if (!this.livestreamCallbacks.has(cameraId)) {
            this.livestreamCallbacks.set(cameraId, new Set());
        }
        this.livestreamCallbacks.get(cameraId).add(callback);
        return () => {
            const set = this.livestreamCallbacks.get(cameraId);
            if (set) {
                set.delete(callback);
                if (set.size === 0) this.livestreamCallbacks.delete(cameraId);
            }
        };
    }

    notifyUpdateListeners() {
        const data = {
            connected: this.connected,
            cameras: this.cameras
        };
        for (const cb of this.updateCallbacks) {
            try { cb(data); } catch (err) { console.error('[CameraMonitor] Update callback error:', err); }
        }
    }

    _notifySnapshotListeners(cameraId, imageBase64) {
        for (const cb of this.snapshotCallbacks) {
            try { cb({ cameraId, imageBase64 }); } catch (err) { console.error('[CameraMonitor] Snapshot callback error:', err); }
        }
    }

    _notifyClassificationListeners(data) {
        for (const cb of this.classificationCallbacks) {
            try { cb(data); } catch (err) { console.error('[CameraMonitor] Classification callback error:', err); }
        }
    }

    // =====================================
    // Getters
    // =====================================

    isConnected() {
        return this.connected;
    }

    getCameraList() {
        return this.cameras;
    }

    getLatestSnapshot(cameraId) {
        return this.snapshots.get(cameraId) || null;
    }

    getRecentClassifications() {
        return this.classifications;
    }

    // =====================================
    // Cleanup
    // =====================================

    async cleanup() {
        console.log('[CameraMonitor] Cleaning up...');
        this.cameras = [];
        this.snapshots.clear();
        this.classifications = [];
        this.updateCallbacks.clear();
        this.snapshotCallbacks.clear();
        this.classificationCallbacks.clear();
        this.livestreamCallbacks.clear();
        this._pendingRequests.clear();
    }
}
